import { CharacterMetadata, ContentBlock, ContentState, convertFromHTML } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';

/**
 * Converts between the rich text editor's content and HTML for public page copy (paragraphs, bold,
 * italics, links and lists — the API sanitizes to the same allowlist). Used when the editor runs in
 * `format='html'`; every other editor keeps storing Draft.js raw JSON.
 */

// @types/draft-js re-exports ContentState via an `import =` alias, which this TS setup only sees as a value.
type ContentStateInstance = InstanceType<typeof ContentState>;
type ContentBlockInstance = InstanceType<typeof ContentBlock>;
type CharacterMetadataInstance = InstanceType<typeof CharacterMetadata>;

/** Page descriptions allow 1,000 visible characters (markup doesn't count); the API enforces the same. */
export const PUBLIC_DESCRIPTION_MAX_LENGTH = 1_000;

const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/i;

/** Whether the text contains anything that looks like an HTML tag (plain-text copy has none). */
export const hasHtmlMarkup = (text: string): boolean => HTML_TAG_PATTERN.test(text);

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&nbsp;': ' ',
};

/** The length a reader sees: tags removed and common entities counted as one character. */
export const pageCopyVisibleTextLength = (text: string | null | undefined): number => {
  if (!text) return 0;
  if (!hasHtmlMarkup(text)) return text.length;
  return text.replace(/<[^>]*>/g, '').replace(/&(?:amp|lt|gt|quot|#39|nbsp);/g, (entity) => ENTITIES[entity] ?? entity)
    .length;
};

/**
 * Imported links get the `text` the link dialog reads (convertFromHTML only sets the URL), so editing a
 * saved link opens the dialog with its label filled in.
 */
const withLinkText = (contentState: ContentStateInstance): ContentStateInstance => {
  let next = contentState;
  next.getBlocksAsArray().forEach((block: ContentBlockInstance) => {
    block.findEntityRanges(
      (character: CharacterMetadataInstance) => {
        const entityKey = character.getEntity();
        return !!entityKey && next.getEntity(entityKey).getType() === 'LINK';
      },
      (start: number, end: number) => {
        const entityKey = block.getEntityAt(start);
        next = next.mergeEntityData(entityKey, { text: block.getText().slice(start, end) });
      },
    );
  });
  return next;
};

/** Builds editor content from stored copy. Plain text keeps its line breaks, one paragraph per line. */
export const pageCopyHtmlToContentState = (value: string | null | undefined): ContentStateInstance => {
  if (!value || !hasHtmlMarkup(value)) return ContentState.createFromText(value ?? '');
  const { contentBlocks, entityMap } = convertFromHTML(value);
  return contentBlocks?.length
    ? withLinkText(ContentState.createFromBlockArray(contentBlocks, entityMap))
    : ContentState.createFromText('');
};

/**
 * Serializes editor content with draft-js-export-html (also used by notes). Returns '' when there is
 * no text, and drops the newlines/indentation it adds between blocks so they don't count as visible text.
 */
export const contentStateToPageCopyHtml = (contentState: ContentStateInstance): string =>
  contentState.getPlainText().trim() ? stateToHTML(contentState).replace(/>\n\s*</g, '><') : '';
